import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  SymbolAnalysisPayload,
  SymbolAnalysisRuntime,
} from '../../core/types/payloads/symbol_analysis.types';
import type { ScoreRenderEvent } from '../../core/music/vex/vexFlowMapper';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { normalizeTokenArray } from '../../core/music/tokens';

function normalizeOptions(raw: unknown): SymbolAnalysisPayload['options'] {
  if (!Array.isArray(raw) || raw.length < 2) {
    throw new Error('SymbolAnalysis requires at least two options');
  }
  const options = raw.map((row, i) => {
    const opt = row as { id?: string; text?: string; isCorrect?: boolean };
    return {
      id: String(opt.id || `opt_${i + 1}`),
      text: String(opt.text || '').trim(),
      isCorrect: opt.isCorrect === true,
    };
  });
  if (!options.some((o) => o.isCorrect)) {
    throw new Error('SymbolAnalysis requires one correct option');
  }
  if (options.some((o) => !o.text)) {
    throw new Error('SymbolAnalysis options require text');
  }
  return options;
}

function normalizeScoreEvents(raw: unknown): ScoreRenderEvent[] | undefined {
  if (!Array.isArray(raw) || !raw.length) return undefined;
  return raw as ScoreRenderEvent[];
}

function normalizeScoreEventsByBar(
  raw: unknown,
): Record<number, ScoreRenderEvent[]> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<number, ScoreRenderEvent[]> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(key);
    if (Number.isFinite(n) && Array.isArray(value) && value.length) {
      out[n] = value as ScoreRenderEvent[];
    }
  }
  return Object.keys(out).length ? out : undefined;
}

export function validateSymbolAnalysisPayload(raw: unknown): SymbolAnalysisPayload {
  if (!raw || typeof raw !== 'object') throw new Error('SymbolAnalysis payload must be an object');
  const data = raw as Partial<SymbolAnalysisPayload>;
  const title = String(data.title || '').trim();
  if (!title) throw new Error('SymbolAnalysis requires title');

  const patternTokens = normalizeTokenArray(data.patternTokens);
  if (!patternTokens.length) throw new Error('SymbolAnalysis requires patternTokens');

  const ts = String(data.timeSignature || '4/4');
  const timeSignature =
    ts === '3/4' || ts === '2/4' || ts === '4/4' ? (ts as SymbolAnalysisPayload['timeSignature']) : '4/4';

  const modifiersRaw = (data.modifiers && typeof data.modifiers === 'object' ? data.modifiers : {}) as
    SymbolAnalysisPayload['modifiers'];
  const ties = Array.isArray(modifiersRaw.ties)
    ? modifiersRaw.ties
        .map((pair) => {
          const row = pair as [number, number];
          return [Number(row[0]), Number(row[1])] as [number, number];
        })
        .filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && a >= 0 && b >= 0 && a < b)
    : [];

  const rhythmDots = Array.isArray(modifiersRaw.rhythmDots)
    ? modifiersRaw.rhythmDots.map(Number).filter((i) => Number.isFinite(i) && i >= 0 && i < patternTokens.length)
    : [];

  const highlightRaw = data.highlightedElement;
  if (!highlightRaw || typeof highlightRaw !== 'object') {
    throw new Error('SymbolAnalysis requires highlightedElement');
  }
  const highlightType = (highlightRaw as { type?: string }).type;
  const type =
    highlightType === 'tie' || highlightType === 'dot' || highlightType === 'key_signature'
      ? highlightType
      : 'dot';
  const tokenIndex =
    typeof (highlightRaw as { tokenIndex?: number }).tokenIndex === 'number'
      ? (highlightRaw as { tokenIndex: number }).tokenIndex
      : undefined;

  const questionText = String(data.questionText || '').trim();
  if (!questionText) throw new Error('SymbolAnalysis requires questionText');

  const phraseBarNumbers = Array.isArray(data.phraseBarNumbers)
    ? data.phraseBarNumbers.map(Number).filter((n) => Number.isFinite(n) && n >= 1)
    : undefined;
  const barTokenStarts = Array.isArray(data.barTokenStarts)
    ? data.barTokenStarts.map(Number).filter((n) => Number.isFinite(n) && n >= 0)
    : undefined;

  return {
    title,
    timeSignature,
    targetBarNumber:
      typeof data.targetBarNumber === 'number' && data.targetBarNumber >= 1
        ? data.targetBarNumber
        : 1,
    patternTokens,
    phraseBarNumbers: phraseBarNumbers?.length ? phraseBarNumbers : undefined,
    barTokenStarts: barTokenStarts?.length ? barTokenStarts : undefined,
    scoreEvents: normalizeScoreEvents(data.scoreEvents),
    scoreEventsByBar: normalizeScoreEventsByBar(data.scoreEventsByBar),
    modifiers: {
      ties,
      keySignature: modifiersRaw.keySignature ? String(modifiersRaw.keySignature) : undefined,
      rhythmDots,
    },
    highlightedElement: {
      type,
      tokenIndex,
      label: String((highlightRaw as { label?: string }).label || 'A').trim() || 'A',
    },
    questionText,
    options: normalizeOptions(data.options),
    rhythmMethod:
      data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly' ? data.rhythmMethod : 'tradicional',
    graphicMode: data.graphicMode === 'symbols-labels' ? 'symbols-labels' : 'symbols',
  };
}

export function createInitialSymbolAnalysisRuntime(
  _payload: SymbolAnalysisPayload,
  _routing: RoutingMode,
): SymbolAnalysisRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const symbolAnalysisLogic: ActivityLogicPlugin<'symbol_analysis'> = {
  mode: 'symbol_analysis',
  validatePayload: validateSymbolAnalysisPayload,
  createInitialRuntime: createInitialSymbolAnalysisRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    const pid = participant.participantId;

    if (input.action === 'FILL') {
      const optionId = String((input.data as { value?: string })?.value ?? '').trim();
      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [pid]: optionId },
        },
      };
    }

    if (input.action === 'SUBMIT') {
      const chosen = ctx.runtime.responses[pid] ?? '';
      const correctOpt = ctx.payload.options.find((o) => o.isCorrect);
      const ok = Boolean(correctOpt && chosen === correctOpt.id);
      const finalized = { ...ctx.runtime.finalized, [pid]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: { submitted, correct: ok ? 100 : 0 },
        },
      };
    }

    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoSymbolAnalysisPayload(): SymbolAnalysisPayload {
  return validateSymbolAnalysisPayload({
    title: 'Análisis de símbolos',
    timeSignature: '3/4',
    targetBarNumber: 1,
    patternTokens: ['Q', 'Q', 'E', 'E'],
    modifiers: { ties: [[0, 1]], rhythmDots: [1], keySignature: 'G' },
    highlightedElement: { type: 'tie', tokenIndex: 0, label: 'A' },
    questionText: '¿Qué función cumple la ligadura señalada con la letra A?',
    options: [
      { id: 'a', text: 'Indica silencio durante un tiempo completo.', isCorrect: false },
      {
        id: 'b',
        text: 'Suma la duración de ambas figuras, prolongando el sonido.',
        isCorrect: true,
      },
      { id: 'c', text: 'Señala que la nota debe tocarse staccato.', isCorrect: false },
    ],
  });
}
